import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './HomePage.module.css';
import { Helmet } from 'react-helmet';

const TermsOfUsePage: React.FC = () => {
    const navigate = useNavigate();

    const navigateToSection = (sectionId: string) => {
        navigate(`/#${sectionId}`);
    };

    const startNewChat = () => {
        navigate('/chat');
    };

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    return (
        <div className={styles.homeContainer}>
            <Helmet>
                <meta charSet="UTF-8" />
                <title>Условия использования - GPT4Chats</title>
                <meta
                    name="description"
                    content="Условия использования GPT4Chats: правила работы с ИИ-помощником на базе GPT-4, ограничения ответственности и ваши обязательства."
                />
                <meta
                    name="keywords"
                    content="условия использования, GPT4Chats, ИИ-помощник, GPT-4, правила сервиса, пользовательское соглашение"
                />
                <meta name="robots" content="index, follow" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <link rel="canonical" href="https://gpt4chats.ru/terms-of-use" />
                <meta property="og:title" content="Условия использования - GPT4Chats" />
                <meta
                    property="og:description"
                    content="Ознакомьтесь с условиями использования GPT4Chats: правила, ограничения и обязательства при работе с ИИ-помощником."
                />
                <meta property="og:url" content="https://gpt4chats.ru/terms-of-use" />
                <meta property="og:type" content="website" />
                <meta property="og:site_name" content="GPT4Chats" />
            </Helmet>
            <header className={styles.homeHeader}>
                <div className={styles.container}>
                    <div className={styles.headerContent}>
                        <div className={styles.homeLogo}>
                            <h1>
                                GPT4Chats<span>.</span>
                            </h1>
                        </div>
                        <nav className={styles.homeNav}>
                            <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('benefits')}>
                                Преимущества
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('use-cases')}>
                                Примеры
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                                Вопросы
                            </button>
                            <button className={styles.startChatBtnSmall} onClick={startNewChat}>
                                Начать
                            </button>
                        </nav>
                    </div>
                </div>
            </header>

            <section className={styles.privacySection}>
                <div className={styles.container}>
                    <h1>Условия использования</h1>
                    <p className={styles.introText}>
                        Используя сервис GPT4Chats, вы соглашаетесь с настоящими Условиями использования. Пожалуйста, внимательно ознакомьтесь с ними перед началом использования платформы.
                    </p>

                    <div className={styles.policyContent}>
                        <h2>1. Общие положения</h2>
                        <p>
                            Настоящие Условия использования регулируют ваше взаимодействие с сервисом GPT4Chats, доступным на сайте GPT4Chats.ru. Использование сервиса означает ваше полное согласие с этими условиями.
                        </p>
                        <ul>
                            <li>Сервис предоставляется "как есть" и предназначен для личного и коммерческого использования в рамках закона.</li>
                            <li>Мы оставляем за собой право отказать в доступе к сервису без объяснения причин.</li>
                        </ul>

                        <h2>2. Правила использования</h2>
                        <p>
                            Вы соглашаетесь использовать сервис исключительно в законных целях и обязуетесь:
                        </p>
                        <ul>
                            <li>Не нарушать права третьих лиц, включая авторские права, права на конфиденциальность и интеллектуальную собственность.</li>
                            <li>Не использовать сервис для распространения вредоносного ПО, спама или незаконного контента.</li>
                            <li>Не пытаться взломать или обойти системы безопасности сервиса.</li>
                        </ul>
                        <p>
                            Нарушение этих правил может привести к немедленному прекращению доступа к сервису.
                        </p>

                        <h2>3. Платные услуги</h2>
                        <p>
                            Некоторые функции сервиса доступны только на платной основе. Используя их, вы соглашаетесь:
                        </p>
                        <ul>
                            <li>Оплачивать услуги в соответствии с установленными тарифами.</li>
                            <li>Предоставлять точные платёжные данные.</li>
                            <li>Не требовать возврата средств за уже оказанные услуги, если иное не предусмотрено законом.</li>
                        </ul>

                        <h2>4. Ограничение ответственности</h2>
                        <p>
                            Сервис GPT4Chats предоставляется "как есть" и "как доступно". Мы не несем ответственности за:
                        </p>
                        <ul>
                            <li>Любые убытки, прямые или косвенные, включая упущенную выгоду, возникшие в результате использования или невозможности использования сервиса.</li>
                            <li>Точность, полноту или актуальность предоставленных ИИ ответов, так как они генерируются автоматически на основе доступных данных.</li>
                            <li>Сбои в работе сервиса, вызванные техническими проблемами, действиями третьих лиц, форс-мажорными обстоятельствами или другими факторами, находящимися вне нашего контроля.</li>
                            <li>Любые последствия, связанные с использованием информации, полученной через сервис.</li>
                        </ul>
                        <p>
                            Вы используете сервис на свой страх и риск. Мы не гарантируем бесперебойную работу сервиса или его пригодность для конкретных целей.
                        </p>

                        <h2>5. Интеллектуальная собственность</h2>
                        <p>
                            Все права на сервис, включая программное обеспечение, дизайн и контент, принадлежат GPT4Chats. Вы получаете ограниченную лицензию на использование сервиса в личных целях, но не имеете права:
                        </p>
                        <ul>
                            <li>Копировать или распространять элементы сервиса без разрешения.</li>
                            <li>Модифицировать или декомпилировать программное обеспечение.</li>
                        </ul>

                        <h2>6. Изменения условий</h2>
                        <p>
                            Мы оставляем за собой право изменять эти Условия использования в любой момент. Обновлённые условия вступают в силу с момента их публикации на этой странице. Продолжая использовать сервис после изменений, вы подтверждаете согласие с новыми условиями.
                        </p>

                        <h2>7. Прекращение использования</h2>
                        <p>
                            Вы можете прекратить использование сервиса в любое время. Мы также можем прекратить предоставление доступа к сервису, если:
                        </p>
                        <ul>
                            <li>Вы нарушаете настоящие Условия.</li>
                            <li>Это необходимо для соблюдения закона или защиты интересов GPT4Chats.</li>
                        </ul>

                        <h2>8. Контакты</h2>
                        <p>
                            Если у вас есть вопросы по поводу этих Условий использования, свяжитесь с нами:
                        </p>
                        <ul>
                            <li>Email: support@gpt4chats.ru</li>
                        </ul>
                    </div>

                    <div className={styles.policyFooter}>
                        <button className={styles.footerLink} onClick={() => navigate('/')}>
                            На главную
                        </button>
                        <span className={styles.footerSep}>•</span>
                        <button
                            className={styles.footerLink}
                            onClick={() => navigate('/privacy-policy')}
                        >
                            Политика конфиденциальности
                        </button>
                    </div>
                </div>
            </section>

            <footer className={styles.homeFooter}>
                <div className={styles.container}>
                    <div className={styles.footerContent}>
                        <div className={styles.footerSection}>
                            <h4>
                                GPT4Chats<span>.</span>
                            </h4>
                            <p>Ваш ИИ-помощник для работы и творчества.</p>
                            <p>GPT4Chats.ru</p>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Навигация</h4>
                            <button className={styles.navLink} onClick={() => navigateToSection('hero')}>
                                Главная
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('features')}>
                                Особенности
                            </button>
                            <button className={styles.navLink} onClick={() => navigateToSection('faq')}>
                                Вопросы
                            </button>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Документы</h4>
                            <button className={styles.navLink} onClick={() => navigate('/privacy-policy')}>
                                Политика конфиденциальности
                            </button>
                            <button className={styles.navLink} onClick={() => navigate('/terms-of-use')}>
                                Условия использования
                            </button>
                        </div>
                        <div className={styles.footerSection}>
                            <h4>Контакты</h4>
                            <p>Email: support@gpt4chats.ru</p>
                        </div>
                    </div>
                    <div className={styles.footerBottom}>
                        <p>© {new Date().getFullYear()} GPT4Chats. Все права защищены.</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default TermsOfUsePage;